import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Edit, KeyRound, Trash2, UserPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { Breadcrumbs, DetailList, Panel } from '@/components/ui/detail-list'
import { UserSelect } from '@/components/ui/user-select'
import { SoftwareAccountForm } from '@/components/forms/SoftwareAccountForm'
import { SoftwareAccountStatusModal } from '@/components/modals/SoftwareAccountStatusModal'
import { apiClient } from '@/lib/api-client'
import { accountStatusColor, accountStatusLabel, billingLabel, nextRenewalDate } from '@/lib/software-format'
import { formatDateShort } from '@shared/utils'
import { toast } from 'react-hot-toast'

export function SoftwareAccountPage() {
  const { accountId } = useParams()
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [statusOpen, setStatusOpen] = useState(false)
  const [userId, setUserId] = useState('')

  const { data: account, isLoading } = useQuery({
    queryKey: ['software-account', accountId],
    queryFn: async () => (await apiClient.get(`/software/accounts/${accountId}`)).data,
    enabled: !!accountId,
  })

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['software-account', accountId] })
    queryClient.invalidateQueries({ queryKey: ['software'] })
  }

  const addUser = useMutation({
    mutationFn: async () => apiClient.post(`/software/accounts/${accountId}/users`, { userId: Number(userId) }),
    onSuccess: () => {
      setUserId('')
      refresh()
      toast.success('User added')
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Could not add the user'),
  })

  const removeUser = useMutation({
    mutationFn: async (memberUserId: number) => apiClient.delete(`/software/accounts/${accountId}/users/${memberUserId}`),
    onSuccess: () => {
      refresh()
      toast.success('User removed')
    },
    onError: (error: any) => toast.error(error.response?.data?.message || 'Could not remove the user'),
  })

  if (isLoading) {
    return <div className="flex h-64 items-center justify-center"><LoadingSpinner size="lg" /></div>
  }

  if (!account) {
    return (
      <div className="py-16 text-center">
        <KeyRound className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
        <p className="text-sm font-medium">Paid account not found</p>
        <Button asChild variant="outline" className="mt-4"><Link to="/software">Back to software</Link></Button>
      </div>
    )
  }

  const software = account.software
  const members = account.members || []
  const nextRenewal = nextRenewalDate(account)
  const crumbs: { label: string; to?: string }[] = [{ label: 'Software', to: '/software' }]
  if (software) crumbs.push({ label: software.name, to: `/software/${software.id}` })
  crumbs.push({ label: account.accountName })

  return (
    <div className="space-y-4">
      <Breadcrumbs items={crumbs} />

      <div className="flex flex-col gap-3 border-b border-border/70 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          <h1 className="truncate text-xl font-semibold tracking-tight">{account.accountName}</h1>
          <Badge className={accountStatusColor(account.status)}>{accountStatusLabel(account.status)}</Badge>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setStatusOpen(true)}>
            Update status
          </Button>
          <Button variant="outline" onClick={() => setEditing(true)}>
            <Edit className="mr-2 h-4 w-4" />
            Edit account
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Account details" className="lg:col-span-2">
          <DetailList items={[
            { label: 'Email', value: account.email },
            {
              label: 'Software',
              value: software ? (
                <Link to={`/software/${software.id}`} className="text-primary hover:underline">{software.name}</Link>
              ) : null,
            },
            { label: 'Vendor', value: software?.vendor },
            { label: 'Plan', value: account.plan },
            { label: 'Billing', value: billingLabel(account) },
            {
              label: 'Next renewal',
              value: account.billingCycle === 'one-time'
                ? 'Does not renew'
                : nextRenewal ? formatDateShort(nextRenewal) : null,
            },
            { label: 'Status reason', value: account.statusReason },
            { label: 'Status changed', value: account.statusChangedAt ? formatDateShort(account.statusChangedAt) : null },
            { label: 'Notes', value: account.notes },
            { label: 'Added', value: account.createdAt ? formatDateShort(account.createdAt) : null },
          ]} />
        </Panel>

        <Panel title={`Users (${members.length})`} className="self-start">
          <div className="space-y-3 border-b border-border p-4">
            <p className="text-xs text-muted-foreground">Everyone listed here can use this paid account.</p>
            <UserSelect value={userId} onChange={setUserId} placeholder="Search for a user" />
            <Button className="w-full" size="sm" disabled={!userId || addUser.isPending} onClick={() => addUser.mutate()}>
              <UserPlus className="mr-2 h-4 w-4" />
              Add user
            </Button>
          </div>
          {members.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">No users yet</p>
          ) : (
            <ul className="divide-y divide-border/70">
              {members.map((member: any) => (
                <li key={member.id} className="flex items-center justify-between gap-3 px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{member.user?.name}</p>
                    <p className="truncate text-xs text-muted-foreground">{member.user?.email}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-destructive"
                    aria-label={`Remove ${member.user?.name || 'user'}`}
                    onClick={() => {
                      if (window.confirm(`Remove ${member.user?.name || 'this user'} from this account?`)) {
                        removeUser.mutate(member.userId)
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <SoftwareAccountStatusModal
        isOpen={statusOpen}
        account={account}
        onClose={() => setStatusOpen(false)}
        onSuccess={refresh}
      />
      <SoftwareAccountForm
        isOpen={editing}
        softwareId={account.softwareId}
        account={editing ? account : null}
        onClose={() => setEditing(false)}
        onSuccess={refresh}
      />
    </div>
  )
}
